import React from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, CheckCircle2Icon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
export interface ConnectedBank {
  id: string;
  name: string;
  logoUrl: string;
  accountType: string;
  accountLastFour: string;
  connectedDate: string;
}
export interface BankConnectionSelectionScreenProps {
  merchantName: string;
  merchantLogo?: string;
  connectedBanks: ConnectedBank[];
  onSelectExistingBank: (bankId: string) => void;
  onConnectNewBank: () => void;
  onCancel?: () => void;
}
export function BankConnectionSelectionScreen({
  merchantName,
  merchantLogo,
  connectedBanks,
  onSelectExistingBank,
  onConnectNewBank,
  onCancel
}: BankConnectionSelectionScreenProps) {
  const hasConnectedBanks = connectedBanks.length > 0;
  return <AnimatedScreen data-id="element-250">
      <div className="space-y-6" data-id="element-251">
        {/* Header */}
        <div data-id="element-252">
          <h2 className="text-xl font-bold mb-2 leading-tight" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-253">
            Choose Payment Source
          </h2>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-254">
            Select which bank account to use for {merchantName} payments
          </p>
        </div>

        {/* Merchant Info */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="p-4 border-2 rounded-2xl" style={{
        background: OPENFINANCE_THEME.gradients.card,
        borderColor: OPENFINANCE_THEME.colors.primary200
      }} data-id="element-255">
          <div className="flex items-center gap-3" data-id="element-256">
            {merchantLogo && <div className="w-12 h-12 rounded-xl flex items-center justify-center p-2" style={{
            backgroundColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-257">
                <img src={merchantLogo} alt={merchantName} className="w-full h-full object-contain" data-id="element-258" />
              </div>}
            <div className="flex-1" data-id="element-259">
              <div className="font-bold text-base" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-260">
                {merchantName}
              </div>
              <div className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-261">
                Recurring bill payment
              </div>
            </div>
          </div>
        </motion.div>

        {/* Connected Banks Section */}
        {hasConnectedBanks && <motion.div variants={staggerContainer} initial="hidden" animate="show" data-id="element-262">
            <div className="flex items-center gap-2 mb-3" data-id="element-263">
              <ShieldCheckIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-264" />
              <div className="text-sm font-bold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-265">
                Your Connected Banks
              </div>
            </div>

            <div className="space-y-3" data-id="element-266">
              {connectedBanks.map(bank => <motion.button key={bank.id} variants={staggerItem} onClick={() => onSelectExistingBank(bank.id)} whileHover={{
            y: -2,
            transition: {
              duration: 0.2
            }
          }} whileTap={{
            scale: 0.98
          }} className="w-full p-4 rounded-xl border-2 transition-all hover:shadow-lg text-left" style={{
            borderColor: OPENFINANCE_THEME.colors.border,
            backgroundColor: 'white'
          }} data-id="element-267">
                  <div className="flex items-center gap-4" data-id="element-268">
                    {/* Bank Logo */}
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center p-2 flex-shrink-0" style={{
                backgroundColor: OPENFINANCE_THEME.colors.borderLight
              }} data-id="element-269">
                      <img src={bank.logoUrl} alt={bank.name} className="w-full h-full object-contain" data-id="element-270" />
                    </div>

                    {/* Bank Info */}
                    <div className="flex-1 min-w-0" data-id="element-271">
                      <div className="font-bold text-base mb-1" style={{
                  color: OPENFINANCE_THEME.colors.text
                }} data-id="element-272">
                        {bank.name}
                      </div>
                      <div className="text-sm mb-1" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-273">
                        {bank.accountType}
                      </div>
                      <div className="text-xs font-mono" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-274">
                        ****{bank.accountLastFour}
                      </div>
                    </div>

                    {/* Connected Badge */}
                    <div className="flex flex-col items-end gap-2" data-id="element-275">
                      <div className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1" style={{
                  backgroundColor: OPENFINANCE_THEME.colors.success + '15',
                  color: OPENFINANCE_THEME.colors.success
                }} data-id="element-276">
                        <CheckCircle2Icon className="w-3 h-3" data-id="element-277" />
                        <span data-id="element-278">Connected</span>
                      </div>
                      <ChevronRightIcon className="w-5 h-5" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-279" />
                    </div>
                  </div>
                </motion.button>)}
            </div>
          </motion.div>}

        {/* Divider */}
        {hasConnectedBanks && <div className="relative" data-id="element-280">
            <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-281">
              <div className="w-full border-t-2" style={{
            borderColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-282" />
            </div>
            <div className="relative flex justify-center" data-id="element-283">
              <span className="px-3 text-xs font-semibold uppercase tracking-wide" style={{
            backgroundColor: '#F9FAFB',
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-284">
                Or
              </span>
            </div>
          </div>}

        {/* Connect New Bank */}
        <motion.button initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: hasConnectedBanks ? 0.4 : 0.2
      }} onClick={onConnectNewBank} whileHover={{
        y: -2,
        transition: {
          duration: 0.2
        }
      }} whileTap={{
        scale: 0.98
      }} className="w-full p-6 rounded-xl border-2 transition-all hover:shadow-lg" style={{
        borderColor: OPENFINANCE_THEME.colors.primary500,
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        borderStyle: 'dashed'
      }} data-id="element-285">
          <div className="flex items-center gap-4" data-id="element-286">
            {/* Plus Icon */}
            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{
            backgroundColor: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-287">
              <PlusIcon className="w-8 h-8 text-white" strokeWidth={3} data-id="element-288" />
            </div>

            {/* Text */}
            <div className="flex-1 text-left" data-id="element-289">
              <div className="font-bold text-base mb-1" style={{
              color: OPENFINANCE_THEME.colors.primary700
            }} data-id="element-290">
                Connect a New Bank
              </div>
              <div className="text-sm" style={{
              color: OPENFINANCE_THEME.colors.primary600
            }} data-id="element-291">
                {hasConnectedBanks ? 'Add another bank account for payments' : 'Connect your first bank account to get started'}
              </div>
            </div>

            <ChevronRightIcon className="w-6 h-6" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-292" />
          </div>
        </motion.button>

        {/* Info Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="p-4 rounded-xl" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`
      }} data-id="element-293">
          <div className="flex items-start gap-3" data-id="element-294">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-295" />
            <div className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-296">
              <div className="font-semibold mb-1" data-id="element-297">Secure & Private</div>
              <div className="text-xs opacity-90" data-id="element-298">
                Your bank credentials are never shared. All connections use
                secure Open Finance standards with bank-level encryption.
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cancel Button */}
        {onCancel && <button onClick={onCancel} className="relative w-full px-6 py-4 bg-white font-semibold transition-all hover:bg-gray-50 overflow-hidden" style={{
        borderRadius: '50px'
      }} data-id="element-299">
            {/* Gradient border effect */}
            <div className="absolute inset-0 rounded-full p-[2px]" style={{
          background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude'
        }} data-id="element-300" />
            <span className="relative z-10" style={{
          background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }} data-id="element-301">
              Back to Bill Selection
            </span>
          </button>}
      </div>
    </AnimatedScreen>;
}